import { useEffect } from "react";
import Header from "@/components/Header";
import LegalBanner from "@/components/LegalBanner";
import Footer from "@/components/Footer";
import { Seo } from "@/lib/seo";

export default function DeleteAccount() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen bg-white text-stone-900 font-sans antialiased" data-testid="delete-account-page">
      <Seo
        title="Delete your account · DriveClear"
        description="How to delete your DriveClear account and the data tied to it — in the app or by email — and exactly what is deleted and what is retained."
        path="/delete-account"
      />
      <LegalBanner />
      <Header />
      <main className="py-14 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight mb-2">Delete your DriveClear account</h1>
          <p className="text-sm text-stone-500 mb-8">DriveClear · published by DriveClear LLC</p>

          <p className="text-sm text-stone-600 leading-relaxed mb-8">
            You can delete your DriveClear account and the data tied to it at any time. There are two ways to do it.
          </p>

          <h2 className="text-xl font-extrabold text-stone-900 tracking-tight mb-3">Option 1 — delete it yourself in the app</h2>
          <ol className="list-decimal pl-5 space-y-2 mb-4 text-sm text-stone-600 leading-relaxed">
            <li>Open the DriveClear app and sign in.</li>
            <li>Go to the Settings tab.</li>
            <li>Scroll to the bottom and tap <strong>Delete my account</strong>.</li>
            <li>Confirm when asked. Your account is removed immediately.</li>
          </ol>
          <p className="text-sm text-stone-600 leading-relaxed mb-8">
            If you have an active subscription, cancel it separately in the store that bills you — Play Store &rsaquo; Payments and subscriptions, or iPhone Settings &rsaquo; your name &rsaquo; Subscriptions. Deleting your DriveClear account does not cancel that billing.
          </p>

          <h2 className="text-xl font-extrabold text-stone-900 tracking-tight mb-3">Option 2 — ask us to delete it</h2>
          <p className="text-sm text-stone-600 leading-relaxed mb-8">
            Email <a href="mailto:Customersupport@driveclearapp.com?subject=Delete%20my%20account" data-testid="delete-account-email" className="font-semibold text-blue-700 hover:text-blue-800">Customersupport@driveclearapp.com</a> from the address on your account with the subject <strong>Delete my account</strong>. We confirm and complete the deletion within 30 days, usually within one business day.
          </p>

          <h2 className="text-xl font-extrabold text-stone-900 tracking-tight mb-3">What gets deleted</h2>
          <ul className="list-disc pl-5 space-y-2 mb-4 text-sm text-stone-600 leading-relaxed">
            <li>Your account and sign-in credentials (name, email address, password).</li>
            <li>Everything you entered about your money: income, debts, down payment, self-reported credit score and all saved affordability results.</li>
            <li>Your Garage vehicles, Dream Garage goals, savings progress, targets, saved deals and comparisons.</li>
            <li>Photos you uploaded for Deal Check. Images are processed and discarded, so nothing remains after deletion.</li>
            <li>Your in-app activity history and email preferences.</li>
          </ul>
          <p className="text-sm text-stone-600 leading-relaxed mb-8">
            All of the above is deleted permanently and cannot be recovered. There is no additional retention period for it.
          </p>

          <h2 className="text-xl font-extrabold text-stone-900 tracking-tight mb-3">What we keep, and for how long</h2>
          <ul className="list-disc pl-5 space-y-2 mb-8 text-sm text-stone-600 leading-relaxed">
            <li><strong>Purchase and subscription records</strong> — kept for up to 7 years in de-identified form, because tax and accounting law requires it. These records are not linked back to your name or email.</li>
            <li><strong>Crash and diagnostic logs</strong> — anonymous technical reports held by our crash-reporting provider (Sentry) for up to 90 days, then deleted automatically. They contain no financial information.</li>
            <li><strong>Email suppression entries</strong> — if you unsubscribed, we keep a one-way hash of your email address so we never contact you again.</li>
          </ul>

          <p className="text-sm text-stone-600 leading-relaxed">
            Questions about any of this: <a href="mailto:Customersupport@driveclearapp.com" data-testid="delete-account-questions-email" className="font-semibold text-blue-700 hover:text-blue-800">Customersupport@driveclearapp.com</a>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
